import clsx from "clsx";
import { Link } from "@inertiajs/react";
import type { ComponentProps, ReactNode } from "react";

export type ButtonVariant =
  | "primary"
  | "brand"
  | "secondary"
  | "ghost"
  | "danger";
export type ButtonSize = "sm" | "md" | "lg";

// Controls in the Boba Bash design are square-cornered, 1-2px brown-bordered
// and sit on a hard offset shadow that collapses when pressed.
const BASE =
  "inline-flex cursor-pointer items-center justify-center gap-2 border-2 border-line font-display font-bold " +
  "shadow-control transition-all duration-fast ease-standard " +
  "hover:-translate-y-px hover:shadow-[0_3px_0_0_rgba(0,0,0,0.25)] " +
  "active:translate-y-0.5 active:shadow-none " +
  "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-control";

const VARIANTS: Record<ButtonVariant, string> = {
  // The Figma CTA is a soft green→blue vertical gradient.
  primary:
    "text-ink-muted bg-[linear-gradient(180deg,#c0dfc6_0%,#85c5da_44%,#6fbbe2_81%,#b6dbc9_100%)]",
  // Goldenrod: the design's second CTA weight, used where a control needs to
  // carry more than "secondary" without competing with the primary gradient.
  brand: "bg-goldenrod text-ink",
  secondary: "bg-surface text-ink hover:bg-goldenrod",
  ghost:
    "border-transparent bg-transparent text-ink shadow-none hover:bg-surface-alt",
  danger: "bg-sunset-orange text-white",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-caption",
  md: "px-5 py-2.5 text-body",
  lg: "px-8 py-3.5 text-subheading",
};

function classesFor(
  variant: ButtonVariant,
  size: ButtonSize,
  className?: string,
) {
  return clsx(BASE, VARIANTS[variant], SIZES[size], className);
}

type BaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
  className?: string;
};

export default function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...props
}: BaseProps & ComponentProps<"button">) {
  return (
    <button className={classesFor(variant, size, className)} {...props}>
      {children}
    </button>
  );
}

// Same skin, but renders an Inertia <Link> so navigation and method-verbs work.
export function ButtonLink({
  variant = "primary",
  size = "md",
  className,
  children,
  ...props
}: BaseProps & Omit<ComponentProps<typeof Link>, "size">) {
  return (
    <Link className={classesFor(variant, size, className)} {...props}>
      {children}
    </Link>
  );
}

// For plain anchors (external links, mailto, non-Inertia routes).
export function ButtonAnchor({
  variant = "primary",
  size = "md",
  className,
  children,
  ...props
}: BaseProps & ComponentProps<"a">) {
  return (
    <a className={classesFor(variant, size, className)} {...props}>
      {children}
    </a>
  );
}
